import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { HELP_TYPES, HELP_TYPE_LABELS, type HelpType } from '@ready/shared';
import { useApi, type InboxScope } from '@/lib/api';
import { formatDateTime } from '@/lib/dates';
import { useDebounced } from '@/lib/use-debounced';
import { Badge, Card, ErrorText } from '@/components/ui';
import { SearchBox } from '@/components/catalog';
import { Filter, Pager } from '@/components/people';
import { StatusPill } from '@/pages/help';

/*
 * A helpdesk inbox (the platform's or an org's): requests with something new
 * first, then by latest activity. Opening one shows the conversation.
 */

const PAGE = 25;
const VIEWS = [
  ['open', 'Open'],
  ['mine', 'Assigned to me'],
  ['unassigned', 'Unassigned'],
  ['done', 'Resolved & closed'],
  ['all', 'All'],
] as const;

export function HelpInbox({ scope }: { scope: InboxScope }) {
  const api = useApi();
  const key = scope.kind === 'platform' ? 'platform' : scope.orgId;
  const [view, setView] = useState<(typeof VIEWS)[number][0]>('open');
  const [type, setType] = useState('');
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const [page, setPage] = useState(0);
  const query = {
    view,
    type: (type || undefined) as HelpType | undefined,
    q: q || undefined,
    offset: page * PAGE,
    limit: PAGE,
  };
  const list = useQuery({
    queryKey: ['helpInbox', key, query],
    queryFn: () => api.helpInbox(scope, query),
    placeholderData: keepPreviousData,
  });
  const refilter = (apply: () => void) => {
    apply();
    setPage(0);
  };
  const items = list.data?.items ?? [];

  return (
    <Card>
      <div className="space-y-3">
        <div
          className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
          role="tablist"
          aria-label="Show"
        >
          {VIEWS.map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              onClick={() => refilter(() => setView(v))}
              className={`min-h-9 shrink-0 rounded-full border px-3 text-sm ${view === v ? 'border-primary bg-primary/15 text-primary-strong' : 'border-border hover:bg-muted'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-[1fr_16rem] sm:items-end">
          <SearchBox
            value={text}
            onChange={(v) => refilter(() => setText(v))}
            placeholder="Search subjects and messages"
            label="Search requests"
          />
          <Filter
            id="help-type-filter"
            label="About"
            value={type}
            onChange={(v) => refilter(() => setType(v))}
            options={[
              ['', 'Anything'],
              ...HELP_TYPES.map((t) => [t, HELP_TYPE_LABELS[t]] as [string, string]),
            ]}
          />
        </div>
      </div>
      <ErrorText error={list.error} />
      <ul className="mt-4 divide-y divide-border">
        {list.isPending && <li className="py-3 text-sm text-muted-foreground">Loading…</li>}
        {list.isSuccess && items.length === 0 && (
          <li className="py-3 text-sm text-muted-foreground">
            {view === 'open' ? 'No open requests. All caught up.' : 'Nothing here.'}
          </li>
        )}
        {items.map((r) => (
          <li key={r.id}>
            <Link
              to={`/help/${r.id}`}
              className="flex items-start justify-between gap-3 py-3 text-sm hover:text-primary"
            >
              <span className="min-w-0">
                <span className={`block truncate ${r.unread ? 'font-semibold' : 'font-medium'}`}>
                  {r.unread && (
                    <span
                      className="mr-1.5 inline-block size-2 rounded-full bg-primary"
                      aria-label="New"
                    />
                  )}
                  {r.subject}
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                  <span className="break-all">{r.requester}</span>
                  <span>· {HELP_TYPE_LABELS[r.type]}</span>
                  <span>· {formatDateTime(r.lastRequesterAt)}</span>
                  {r.assignee && <span>· {r.assignee}</span>}
                  {r.movedFromOrg && <Badge>From an organization</Badge>}
                </span>
              </span>
              <StatusPill status={r.status} admin />
            </Link>
          </li>
        ))}
      </ul>
      <Pager
        page={page}
        pageSize={PAGE}
        total={list.data?.total ?? 0}
        busy={list.isFetching}
        onPage={setPage}
      />
    </Card>
  );
}
